import { useRef } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { CountBadge, Icon, cx, setPendingCapture, useAuth, type IconName } from '@saas/ui';
import type { NavModel } from './nav.js';

/**
 * C-17 タブバー（767px 以下）: 📥 自分宛て / 💬 チャット（段 4・無効）/ [撮影] / 🗂 業務 / ⚙ 設定。
 * 中央はタブではない。押すと領収書の撮影（<input capture>）→ 経費の明細作成へ。
 * 文字サイズ 131% 以上ではラベルを消してアイコンのみ（読み上げラベルは残す・05 §3.3b）。
 * 選択中はアイコンとラベルを text/primary、非選択は text/tertiary。下線やピル背景は付けない。
 */
function Tab({ to, icon, label, end, count, disabled, reason }: { to: string; icon: IconName; label: string; end?: boolean; count?: number; disabled?: boolean; reason?: string }) {
  const inner = (active: boolean) => (
    <>
      <span className="relative">
        <Icon name={icon} size={24} />
        {count ? <CountBadge count={count} className="absolute -right-3 -top-1.5" /> : null}
      </span>
      <span className={cx('text-tab-label [html[data-font-scale=large]_&]:sr-only', active && 'font-semibold')}>{label}</span>
    </>
  );
  if (disabled) {
    return (
      <span className="flex min-h-[var(--tabbar-height)] flex-1 flex-col items-center justify-center gap-0.5 text-fg-tertiary" aria-disabled="true" title={reason} role="link">
        <span className="disabled-look flex flex-col items-center gap-0.5">{inner(false)}</span>
        <span className="sr-only">{reason}</span>
      </span>
    );
  }
  return (
    <NavLink to={to} end={end} aria-label={label} className={({ isActive }) => cx('flex min-h-[var(--tabbar-height)] flex-1 flex-col items-center justify-center gap-0.5', isActive ? 'bg-brand-subtle text-brand' : 'text-fg-tertiary')}>
      {({ isActive }) => inner(isActive)}
    </NavLink>
  );
}

export function MobileTabBar({ model }: { model: NavModel }) {
  const { isModuleEnabled, can } = useAuth();
  const navigate = useNavigate();
  const camRef = useRef<HTMLInputElement>(null);
  const expenseOn = isModuleEnabled('expense');
  // DM とチャンネルだけ（レコード会話はチャットタブに出さない・05 条件 7）
  const chatOn = isModuleEnabled('chat') && (can('chat.dm.use') || can('chat.channel.read'));

  return (
    <nav aria-label="主要なタブ" className="fixed inset-x-0 bottom-0 z-40 border-t border-[var(--sheet-border-color)] bg-card pb-[env(safe-area-inset-bottom)] shadow-elev-2 md:hidden">
      <div className="flex items-stretch">
        <Tab to="/" icon="inbox" label="自分宛て" end count={(model.counts['workflow.inbox'] ?? 0) + (model.counts['notifications.unread'] ?? 0)} />
        <Tab to="/chat" icon="chat_bubble" label="チャット" count={model.counts['chat.unread'] ?? 0} disabled={!chatOn} reason="チャットはこの会社では有効になっていません" />
        <div className="flex flex-1 flex-col items-center justify-end">
          <button
            type="button"
            aria-label="領収書を撮影して経費を登録"
            disabled={!expenseOn}
            onClick={() => camRef.current?.click()}
            className="-mt-6 flex h-14 w-14 items-center justify-center rounded-card bg-brand text-brand-on shadow-float disabled:disabled-look"
          >
            <Icon name="photo_camera" size={28} />
          </button>
          <span className="pb-1 text-tab-label text-fg-tertiary [html[data-font-scale=large]_&]:sr-only" aria-hidden="true">
            撮る
          </span>
          <input
            ref={camRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              if (files.length === 0) return;
              setPendingCapture(files);
              navigate('/expense/new?capture=1');
            }}
          />
        </div>
        <Tab to="/work" icon="home_repair_service" label="業務" />
        <Tab to="/settings" icon="settings" label="設定" />
      </div>
    </nav>
  );
}
